import React from 'react'
import {
    Alert,
    App,
    Button,
    Empty,
    Input,
    Result,
    Select,
    Space,
    Spin,
    Table,
    Tag,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { RangePickerProps } from 'antd/es/date-picker'
import type {
    SorterResult,
    TablePaginationConfig,
} from 'antd/es/table/interface'
import {
    EditOutlined,
    FileImageOutlined,
    ReloadOutlined,
    SearchOutlined,
    SettingOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import RegionSelect from '../components/common/RegionSelect'
import { ReadonlyRangePicker } from '../components/common/DatePickerControls'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../components/common/FilterBar'
import {
    PaginatedListCard,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../components/common/PaginatedListTable'
import { HistoryDetail, HistoryDeviceNameDisplay } from '../components/history'
import { AnnotationModelSettingsModal } from '../components/history/AnnotationModelSettingsModal'
import { PageLayout } from '../components/layout'
import { usePermissions } from '../hooks/usePermissions'
import {
    getAnnotationModelConfig,
    getHistoryRecord,
    useHistory,
} from '../services/historyService'
import { useModelRawClassFilterOptions } from '../services/pestTypeOptionsService'
import { localeService } from '../services/localeService'
import {
    HistoryListItem,
    HistoryAnnotationModelConfig,
    HistoryPrediction,
    HistoryRecord,
    HistoryReviewStatus,
} from '../types/history'
import {
    getHistoryPaginationTotal,
    renderHistoryPaginationTotal,
} from '../utils/historyPagination'
import {
    areNormalizedSearchValuesEqual,
    normalizeSearchText,
    normalizeSearchValues,
} from '../utils/searchText'
import { formatRawClassDisplayName } from '../utils/rawClassDisplay'

interface AnnotationFilterState {
    dateRange: [Dayjs, Dayjs] | null
    regionIds: number[]
    deviceKeyword: string
    pestTypes: string[]
    reviewStatus?: HistoryReviewStatus
}

const createDefaultFilters = (): AnnotationFilterState => ({
    dateRange: null,
    regionIds: [],
    deviceKeyword: '',
    pestTypes: [],
    reviewStatus: undefined,
})

const ANNOTATION_TABLE_SCROLL_X = 1200

const areRegionIdsEqual = (left: number[], right: number[]): boolean =>
    left.length === right.length &&
    left.every((regionId, index) => regionId === right[index])

const areFiltersEqual = (
    left: AnnotationFilterState,
    right: AnnotationFilterState,
): boolean => {
    const leftDateRange = left.dateRange
    const rightDateRange = right.dateRange
    const hasSameDateRange =
        leftDateRange === rightDateRange ||
        (leftDateRange !== null &&
            rightDateRange !== null &&
            leftDateRange[0].valueOf() === rightDateRange[0].valueOf() &&
            leftDateRange[1].valueOf() === rightDateRange[1].valueOf())

    return (
        hasSameDateRange &&
        areRegionIdsEqual(left.regionIds, right.regionIds) &&
        normalizeSearchText(left.deviceKeyword) ===
            normalizeSearchText(right.deviceKeyword) &&
        areNormalizedSearchValuesEqual(left.pestTypes, right.pestTypes) &&
        left.reviewStatus === right.reviewStatus
    )
}

const getReviewTagColor = (status: HistoryReviewStatus): string => {
    switch (status) {
        case 'confirmed':
            return 'green'
        case 'missed_detection':
            return 'volcano'
        case 'incorrect_detection':
            return 'orange'
        case 'needs_review':
            return 'blue'
        default:
            return 'default'
    }
}

const AnnotationManagement: React.FC = () => {
    const { message } = App.useApp()
    const { t } = useTranslation(['history', 'common', 'navigation', 'admin'])
    const { canReviewHistory } = usePermissions()
    const { options: modelRawClassOptions } = useModelRawClassFilterOptions(
        [],
        canReviewHistory,
    )
    const pestTypeOptions = React.useMemo(
        () =>
            modelRawClassOptions.map((option) => ({
                value: option.value,
                label: formatRawClassDisplayName(option.value, t, option.label),
            })),
        [modelRawClassOptions, t],
    )
    const modelRawClassLocalizedNames = React.useMemo(
        () =>
            Object.fromEntries(
                modelRawClassOptions.map((option) => [
                    option.value.trim().toLowerCase(),
                    option.label,
                ]),
            ),
        [modelRawClassOptions],
    )
    const [previewVisible, setPreviewVisible] = React.useState(false)
    const [previewLoading, setPreviewLoading] = React.useState(false)
    const [previewRecord, setPreviewRecord] =
        React.useState<HistoryRecord | null>(null)
    const [reviewSubmitting, setReviewSubmitting] = React.useState(false)
    const [modelSettingsVisible, setModelSettingsVisible] =
        React.useState(false)
    const [modelSettingsMode, setModelSettingsMode] = React.useState<
        'settings' | 'confirmation'
    >('settings')
    const [annotationModelConfig, setAnnotationModelConfig] =
        React.useState<HistoryAnnotationModelConfig | null>(null)
    const [pendingAnnotationRecord, setPendingAnnotationRecord] =
        React.useState<HistoryListItem | null>(null)
    const annotationModelConfigRequestIdRef = React.useRef(0)
    const [draftFilters, setDraftFilters] =
        React.useState<AnnotationFilterState>(() => createDefaultFilters())
    const [submittedFilters, setSubmittedFilters] =
        React.useState<AnnotationFilterState>(() => createDefaultFilters())
    const [currentPage, setCurrentPage] = React.useState(1)
    const [pageSize, setPageSize] = React.useState(10)
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')
    const [queryTrigger, setQueryTrigger] = React.useState(0)
    const previewRequestIdRef = React.useRef(0)
    const reviewStatusOptions = React.useMemo(
        () =>
            (
                [
                    'pending',
                    'needs_review',
                    'confirmed',
                    'missed_detection',
                    'incorrect_detection',
                ] as const
            ).map((status) => ({
                label: t(`history:review.status.${status}`),
                value: status,
            })),
        [t],
    )

    const {
        records,
        total,
        totalStatus,
        paginationMeta,
        loading,
        error,
        refreshRecords,
        updateReview,
    } = useHistory({
        type: 'image',
        page: currentPage,
        page_size: pageSize,
        sort_by: 'timestamp',
        sort_order: sortOrder,
        start_timestamp: submittedFilters.dateRange
            ? submittedFilters.dateRange[0].valueOf()
            : undefined,
        end_timestamp: submittedFilters.dateRange
            ? submittedFilters.dateRange[1].valueOf()
            : undefined,
        region_ids:
            submittedFilters.regionIds.length > 0
                ? submittedFilters.regionIds
                : undefined,
        device_name: submittedFilters.deviceKeyword.trim() || undefined,
        pest_types:
            submittedFilters.pestTypes.length > 0
                ? submittedFilters.pestTypes
                : undefined,
        review_status: submittedFilters.reviewStatus,
        mode: 'annotation',
        forceRefreshKey: queryTrigger,
    })

    const formatDate = React.useCallback(
        (timestamp: number) =>
            localeService.formatDate(timestamp, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }),
        [],
    )

    const handleDateRangeChange = React.useCallback<
        NonNullable<RangePickerProps['onChange']>
    >((dates) => {
        setDraftFilters((prevFilters) => ({
            ...prevFilters,
            dateRange:
                dates && dates[0] && dates[1]
                    ? ([dates[0], dates[1]] as [Dayjs, Dayjs])
                    : null,
        }))
    }, [])

    const handleSearch = React.useCallback(() => {
        const nextFilters: AnnotationFilterState = {
            dateRange: draftFilters.dateRange,
            regionIds: draftFilters.regionIds,
            deviceKeyword: normalizeSearchText(draftFilters.deviceKeyword),
            pestTypes: normalizeSearchValues(draftFilters.pestTypes),
            reviewStatus: draftFilters.reviewStatus,
        }
        setCurrentPage(1)
        setSubmittedFilters(nextFilters)
        setQueryTrigger((value) => value + 1)
    }, [draftFilters])

    const handleReset = React.useCallback(() => {
        const nextFilters = createDefaultFilters()
        const shouldForceRefresh =
            currentPage === 1 &&
            sortOrder === 'desc' &&
            areFiltersEqual(nextFilters, submittedFilters)

        setDraftFilters(nextFilters)
        setSubmittedFilters(nextFilters)
        setSortOrder('desc')
        setCurrentPage(1)

        if (shouldForceRefresh) {
            void refreshRecords(true)
        }
    }, [currentPage, refreshRecords, sortOrder, submittedFilters])

    const showDetailModal = React.useCallback(
        async (record: HistoryListItem) => {
            if (record.media_expired) {
                message.warning(
                    t('history:messages.mediaExpiredCannotAnnotate'),
                )
                return
            }

            setPreviewVisible(true)
            setPreviewRecord(null)
            setPreviewLoading(true)
            previewRequestIdRef.current += 1
            const requestId = previewRequestIdRef.current

            const detailRecord = await getHistoryRecord(record.id, {
                mode: 'annotation',
            })
            if (requestId === previewRequestIdRef.current) {
                setPreviewRecord(detailRecord)
                setPreviewLoading(false)
            }
        },
        [message, t],
    )

    const loadAnnotationModelConfig = React.useCallback(async () => {
        const requestId = ++annotationModelConfigRequestIdRef.current
        const config = await getAnnotationModelConfig()
        if (requestId !== annotationModelConfigRequestIdRef.current) {
            return null
        }
        if (config) {
            setAnnotationModelConfig(config)
        }
        return config
    }, [])

    const openDetailModal = React.useCallback(
        async (record: HistoryListItem) => {
            if (record.media_expired) {
                message.warning(
                    t('history:messages.mediaExpiredCannotAnnotate'),
                )
                return
            }

            if (annotationModelConfig?.confirmation_required) {
                setPendingAnnotationRecord(record)
                setModelSettingsMode('confirmation')
                setModelSettingsVisible(true)
                return
            }

            const latestConfig = await loadAnnotationModelConfig()
            if (!latestConfig) {
                message.error(
                    t('history:messages.annotationModelConfigLoadFailed'),
                )
                return
            }
            if (latestConfig.confirmation_required) {
                setPendingAnnotationRecord(record)
                setModelSettingsMode('confirmation')
                setModelSettingsVisible(true)
                return
            }
            await showDetailModal(record)
        },
        [
            annotationModelConfig?.confirmation_required,
            loadAnnotationModelConfig,
            message,
            showDetailModal,
            t,
        ],
    )

    React.useEffect(() => {
        if (!canReviewHistory) {
            return
        }
        let active = true
        void loadAnnotationModelConfig().then((config) => {
            if (!active || !config) {
                return
            }
            setAnnotationModelConfig(config)
            if (config.confirmation_required) {
                setModelSettingsMode('confirmation')
                setModelSettingsVisible(true)
            }
        })
        return () => {
            active = false
            annotationModelConfigRequestIdRef.current += 1
        }
    }, [canReviewHistory, loadAnnotationModelConfig])

    const handleModelConfigSaved = React.useCallback(
        (config: HistoryAnnotationModelConfig) => {
            setAnnotationModelConfig(config)
            const record = pendingAnnotationRecord
            setPendingAnnotationRecord(null)
            if (record && !config.confirmation_required) {
                void showDetailModal(record)
            }
        },
        [pendingAnnotationRecord, showDetailModal],
    )

    const columns = React.useMemo<ColumnsType<HistoryListItem>>(
        () => [
            {
                title: t('history:table.uploadTime'),
                key: 'timestamp',
                width: 220,
                render: (_, record) => formatDate(record.timestamp),
                sorter: true,
                sortDirections: ['descend', 'ascend', 'descend'],
                sortOrder: sortOrder === 'asc' ? 'ascend' : 'descend',
            },
            {
                title: t('history:table.fileName'),
                dataIndex: 'filename',
                key: 'filename',
                width: 240,
                ellipsis: true,
                render: (text: string) => (
                    <Space style={{ maxWidth: '100%' }}>
                        <FileImageOutlined />
                        {renderPaginatedListText(text)}
                    </Space>
                ),
            },
            {
                title: t('history:table.regionName'),
                key: 'region_name',
                width: 180,
                ellipsis: true,
                render: (_, record) =>
                    renderPaginatedListText(
                        record.region_name ||
                            t('history:messages.deviceUnavailable'),
                    ),
            },
            {
                title: t('history:table.deviceName'),
                key: 'device_name',
                width: 220,
                ellipsis: true,
                render: (_, record) => (
                    <HistoryDeviceNameDisplay
                        record={record}
                        fallback={t('history:messages.deviceUnavailable')}
                    />
                ),
            },
            {
                title: t('history:table.reviewStatus'),
                key: 'review_status',
                width: 160,
                render: (_, record) => (
                    <Tag color={getReviewTagColor(record.review_status)}>
                        {t(`history:review.status.${record.review_status}`)}
                    </Tag>
                ),
            },
            {
                title: t('history:table.actions'),
                key: 'action',
                width: 120,
                fixed: 'right' as const,
                render: (_, record) => (
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            void openDetailModal(record)
                        }}
                    >
                        {t('history:actions.annotate')}
                    </Button>
                ),
            },
        ],
        [formatDate, openDetailModal, sortOrder, t],
    )

    if (!canReviewHistory) {
        return (
            <PageLayout
                title={t('history:annotationManagementTitle')}
                breadcrumb={[
                    { title: t('navigation:breadcrumb.annotationManagement') },
                ]}
            >
                <Result
                    status="403"
                    title={t('common:featureNotAvailable')}
                    subTitle={t(
                        'history:messages.annotationManagementAccessDenied',
                    )}
                    extra={
                        <Button type="primary">
                            <Link to="/history">{t('history:title')}</Link>
                        </Button>
                    }
                />
            </PageLayout>
        )
    }

    return (
        <PageLayout
            title={t('history:annotationManagementTitle')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.annotationManagement') },
            ]}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <FilterBar>
                    <FilterField label={t('history:filters.uploadTime')}>
                        <ReadonlyRangePicker
                            showTime
                            value={draftFilters.dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY/MM/DD HH:mm:ss"
                        />
                    </FilterField>

                    <FilterField label={t('history:filters.region')}>
                        <RegionSelect
                            multiple
                            mode="annotation"
                            value={draftFilters.regionIds}
                            onChange={(value) => {
                                setDraftFilters((prevFilters) => ({
                                    ...prevFilters,
                                    regionIds: Array.isArray(value)
                                        ? value
                                        : [],
                                }))
                            }}
                            allowClear
                            placeholder={t(
                                'history:filters.regionSearchPlaceholder',
                            )}
                        />
                    </FilterField>

                    <FilterField label={t('history:filters.deviceSearch')}>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            value={draftFilters.deviceKeyword}
                            onChange={(event) => {
                                setDraftFilters((prevFilters) => ({
                                    ...prevFilters,
                                    deviceKeyword: event.target.value,
                                }))
                            }}
                            onPressEnter={handleSearch}
                            placeholder={t(
                                'history:filters.deviceSearchPlaceholder',
                            )}
                        />
                    </FilterField>

                    <FilterField label={t('history:filters.pestType')}>
                        <Select<string[]>
                            allowClear
                            aria-label={t('history:filters.pestType')}
                            maxTagCount="responsive"
                            mode="multiple"
                            value={draftFilters.pestTypes}
                            options={pestTypeOptions}
                            onChange={(value) => {
                                setDraftFilters((prevFilters) => ({
                                    ...prevFilters,
                                    pestTypes: value,
                                }))
                            }}
                            optionFilterProp="label"
                            placeholder={t(
                                'history:filters.pestTypePlaceholder',
                            )}
                            showSearch
                        />
                    </FilterField>

                    <FilterField label={t('history:filters.reviewStatus')}>
                        <Select<HistoryReviewStatus>
                            allowClear
                            value={draftFilters.reviewStatus}
                            options={reviewStatusOptions}
                            onChange={(value) => {
                                setDraftFilters((prevFilters) => ({
                                    ...prevFilters,
                                    reviewStatus: value,
                                }))
                            }}
                            placeholder={t('history:filters.reviewStatus')}
                        />
                    </FilterField>

                    <FilterActions>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={loading}
                            disabled={loading}
                        >
                            {t('history:actions.search')}
                        </Button>
                        <Button onClick={handleReset} disabled={loading}>
                            {t('history:actions.reset')}
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => refreshRecords(true)}
                            loading={loading}
                            disabled={loading}
                        >
                            {t('history:actions.refresh')}
                        </Button>
                    </FilterActions>
                </FilterBar>

                <PaginatedListCard>
                    {annotationModelConfig?.confirmation_required && (
                        <Alert
                            type="warning"
                            showIcon
                            message={t(
                                'history:messages.annotationModelConfirmationPending',
                            )}
                            action={
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setModelSettingsMode('confirmation')
                                        setModelSettingsVisible(true)
                                    }}
                                >
                                    {t(
                                        'history:actions.confirmAnnotationModelSettings',
                                    )}
                                </Button>
                            }
                            style={{ marginBottom: 12 }}
                        />
                    )}
                    <PaginatedListToolbar align="left">
                        <Button
                            icon={<SettingOutlined />}
                            onClick={() => {
                                setModelSettingsMode('settings')
                                setModelSettingsVisible(true)
                            }}
                        >
                            {t('history:actions.annotationModelSettings')}
                        </Button>
                    </PaginatedListToolbar>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 48 }}>
                            <Spin size="large" />
                        </div>
                    ) : error && records.length === 0 ? (
                        <Result
                            status="warning"
                            title={t('history:messages.listLoadFailed')}
                            extra={
                                <Button onClick={() => refreshRecords(true)}>
                                    {t('history:actions.refresh')}
                                </Button>
                            }
                        />
                    ) : records.length === 0 ? (
                        <Empty description={t('history:messages.noRecords')} />
                    ) : (
                        <Table
                            rowKey="id"
                            size={PAGINATED_LIST_TABLE_SIZE}
                            columns={columns}
                            dataSource={records}
                            tableLayout="fixed"
                            scroll={{ x: ANNOTATION_TABLE_SCROLL_X }}
                            onChange={(
                                pagination: TablePaginationConfig,
                                _filters,
                                sorter:
                                    | SorterResult<HistoryListItem>
                                    | SorterResult<HistoryListItem>[],
                            ) => {
                                const resolvedPage =
                                    pagination.current ?? currentPage
                                const resolvedPageSize =
                                    pagination.pageSize ?? pageSize
                                if (
                                    resolvedPage !== currentPage ||
                                    resolvedPageSize !== pageSize
                                ) {
                                    const nextPage =
                                        resolvedPageSize !== pageSize
                                            ? 1
                                            : resolvedPage
                                    setPageSize(resolvedPageSize)
                                    setCurrentPage(nextPage)
                                }

                                const resolvedSorter = Array.isArray(sorter)
                                    ? sorter[0]
                                    : sorter
                                if (
                                    resolvedSorter?.columnKey === 'timestamp' &&
                                    resolvedSorter.order
                                ) {
                                    const nextSortOrder =
                                        resolvedSorter.order === 'ascend'
                                            ? 'asc'
                                            : 'desc'
                                    if (nextSortOrder !== sortOrder) {
                                        setCurrentPage(1)
                                        setSortOrder(nextSortOrder)
                                    }
                                }
                            }}
                            pagination={buildPaginatedListPagination({
                                current: currentPage,
                                pageSize,
                                total: getHistoryPaginationTotal(
                                    paginationMeta,
                                    pageSize,
                                    total,
                                ),
                                showTotal: () =>
                                    renderHistoryPaginationTotal(
                                        (key, options) =>
                                            t(`history:${key}`, options),
                                        {
                                            total,
                                            totalStatus,
                                            pageSize,
                                            meta: paginationMeta,
                                        },
                                    ),
                            })}
                        />
                    )}
                </PaginatedListCard>
            </Space>

            <HistoryDetail
                visible={previewVisible}
                record={previewRecord}
                rawClassLocalizedNames={modelRawClassLocalizedNames}
                loading={previewLoading}
                editable={canReviewHistory}
                requireEditActivation
                canExport={false}
                bypassCapabilityFallback={canReviewHistory}
                readonlyPreviewOnImageClick
                zoomControlPlacement="hidden"
                reviewSubmitting={reviewSubmitting}
                exportSubmitting={false}
                onClose={() => {
                    previewRequestIdRef.current += 1
                    setPreviewVisible(false)
                    setPreviewLoading(false)
                }}
                onUpdateReview={async (
                    status: HistoryReviewStatus,
                    predictions?: HistoryPrediction[],
                    annotationModelOptionKeys?: string[],
                ) => {
                    if (!previewRecord) {
                        return null
                    }

                    setReviewSubmitting(true)
                    try {
                        const updatedRecord = await updateReview(
                            previewRecord.id,
                            {
                                status,
                                predictions,
                                annotation_model_option_keys:
                                    annotationModelOptionKeys,
                            },
                        )
                        if (updatedRecord) {
                            setPreviewRecord(updatedRecord)
                        }
                        return updatedRecord
                    } finally {
                        setReviewSubmitting(false)
                    }
                }}
                onExport={async () => undefined}
                formatDate={formatDate}
            />
            <AnnotationModelSettingsModal
                open={modelSettingsVisible}
                mode={modelSettingsMode}
                onSaved={handleModelConfigSaved}
                onClose={() => {
                    setModelSettingsVisible(false)
                    setPendingAnnotationRecord(null)
                }}
            />
        </PageLayout>
    )
}

export default AnnotationManagement
